import {
    Injectable,
    inject
} from "@angular/core";

import {
    RootScope
} from "app/ajs-upgraded-providers";

import {
    EntityManagerService
} from "src/app/core";

import {
    TranslateService
} from "@ngx-translate/core";

import {
    Vat
} from "tilby-models";

import {
    getVatValue,
    sumDiscounts,
    sumSurcharges
} from "src/app/features/dashboard/components/dashboard/dashboard.utils";

import {
    isObjectEmpty,
    MathUtils
} from "src/app/shared/utils";

import {
    ReducePrinter,
    Today
} from "src/app/features/dashboard/models/dashboard.model";

import {
    TilbyCurrencyPipe
} from "@tilby/tilby-ui-lib/pipes/tilby-currency";

import {
    TilbyDatePipe
} from "@tilby/tilby-ui-lib/pipes/tilby-date";

interface ReportOptions {
    printerColumns?: number;
    vats: Vat[];
}

const sumVatAmounts = (vats: any[] = []) => vats.reduce((sum, vat) => sum + (vat?.amount || 0), 0);

const DEFAULT_PRINTER_COLUMNS = 42;

@Injectable({
    providedIn: 'root'
})
export class DashboardReportGeneratorService {
    private readonly $rootScope = inject(RootScope);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
    private readonly tilbyDatePipe = inject(TilbyDatePipe);
    private readonly translate = inject(TranslateService);

    public async generateReport(printers: ReducePrinter[], todayData: Today) {
        const vats = await this.entityManagerService.vat.fetchCollectionOffline();
        const text: string[] = [];
        const printerColumns = DEFAULT_PRINTER_COLUMNS;

        text.push(
            this.label('TITLE').padEnd(printerColumns, ' '),
            this.tilbyDatePipe.transform(TilbyDatePipe.date()) || '',
            "",
            ""
        );

        for (const printer of printers) {
            text.push(
                `** ${printer.name.toUpperCase()} **`.padEnd(printerColumns, ' '),
                "",
                ...this.getPrinterReport(printer, { vats, printerColumns }),
                "",
                "-".repeat(printerColumns),
                ""
            );
        }

        if (!isObjectEmpty(todayData.credit_notes)) {
            const creditNotesLabel = this.label('CREDIT_NOTES');
            const creditNotesValue = this.currency(todayData.credit_notes?.amount || 0);

            text.push(
                this.row(creditNotesLabel, creditNotesValue, printerColumns),
                ""
            );
        }

        if (!isObjectEmpty(todayData.cash_movements)) {
            text.push(
                this.label('DRAWER_TOTAL'),
                this.row(this.label('DEPOSIT'), this.currency(todayData.cash_movements.total?.income?.amount || 0), printerColumns),
                this.row(this.label('WITHDRAWAL'), this.currency(todayData.cash_movements.total?.outcome?.amount || 0), printerColumns),
                "",
            );
        }

        this.$rootScope.$broadcast('dashboardReport:reportCreated');

        return text.join("\n");
    };

    private currency(amount: number) {
        return (this.tilbyCurrencyPipe.transform(amount) ?? '').padEnd(10, ' ');
    }

    private row(label: string, value: string, printerColumns: number) {
        return label.padEnd(printerColumns - value.length, ' ') + value;
    }

    private label(id: string) {
        return this.translate.instant(`DASHBOARD.EXPORTED_DOCUMENT.${id}`);
    }

    private vatRows(vats: Vat[], printerVats: any[], printerColumns: number) {
        return printerVats.map(vat => this.row(`-- ${getVatValue(vats, vat.name)}`, this.currency(vat.amount || 0), printerColumns));
    }

    public getPrinterReport(printer: ReducePrinter, options: ReportOptions) {
        const vats = options.vats;
        const printerColumns = options?.printerColumns || DEFAULT_PRINTER_COLUMNS;
        const printerReport: string[] = [];

        const fiscalReceiptsVats = printer.fiscal_receipts?.vats ?? [];
        const invoicesVats = printer.invoices?.vats ?? [];
        const hasFiscalReceipts = fiscalReceiptsVats.length > 0;
        const hasInvoices = invoicesVats.length > 0;
        const fiscalReceiptsTotal = sumVatAmounts(fiscalReceiptsVats);
        const invoicesTotal = sumVatAmounts(invoicesVats);
        const fiscalReceiptsCount = Math.trunc(Number(printer.fiscal_receipts?.count)) || 0;
        const invoicesCount = Math.trunc(Number(printer.invoices?.count)) || 0;
        const documentsCount = fiscalReceiptsCount + invoicesCount;
        const saleAverage = documentsCount ? MathUtils.round((fiscalReceiptsTotal + invoicesTotal) / documentsCount) : 0;

        printerReport.push(this.row(this.label('TOTAL'), this.currency(fiscalReceiptsTotal + invoicesTotal), printerColumns));

        if (saleAverage) {
            printerReport.push(this.row(this.label('AVERAGE'), this.currency(saleAverage), printerColumns));
        }

        printerReport.push("");

        if (hasFiscalReceipts) {
            const label = `${this.label('FISCAL_RECEIPTS')} (${fiscalReceiptsCount})`;

            printerReport.push(
                this.row(label, this.currency(fiscalReceiptsTotal), printerColumns),
                ...this.vatRows(vats, fiscalReceiptsVats, printerColumns),
                this.row(this.label('UNCLAIMED_VAT'), this.currency(printer.fiscal_receipts?.unclaimed || 0), printerColumns),
                ""
            );
        }

        if (hasInvoices) {
            const label = `${this.label('INVOICES')} (${invoicesCount})`;

            printerReport.push(
                this.row(label, this.currency(invoicesTotal), printerColumns),
                ...this.vatRows(vats, invoicesVats, printerColumns),
                this.row(this.label('UNCLAIMED_VAT'), this.currency(printer.invoices?.unclaimed || 0), printerColumns),
                ""
            );
        }

        if (printer.summary_rc_invoices?.vats?.length) {
            const label = this.label('SUMMARY_RC_INVOICES');

            printerReport.push(
                label.padEnd(printerColumns - label.length, ' '),
                ...this.vatRows(vats, printer.summary_rc_invoices.vats, printerColumns),
                ""
            );
        }

        if (printer.summary_nrc_invoices?.vats?.length) {
            printerReport.push(
                this.row(this.label('SUMMARY_NRC_INVOICES'), this.currency(sumVatAmounts(printer.summary_nrc_invoices.vats)), printerColumns),
                ""
            );
        }

        if (printer.refunds && !isObjectEmpty(printer.refunds)) {
            printerReport.push(
                this.row(this.label('REFUNDS'), this.currency(Math.abs(printer.refunds.amount || 0)), printerColumns),
                "",
            );
        }

        if (printer.voids && !isObjectEmpty(printer.voids)) {
            printerReport.push(
                this.row(this.label('VOIDS'), this.currency(Math.abs(printer.voids.amount || 0)), printerColumns),
                ""
            );
        }

        if (printer.departments?.length) {
            printerReport.push(this.label('DEPARTMENTS'));

            for (const department of printer.departments) {
                printerReport.push(this.row(`- ${department.name}`, this.currency(department.amount || 0), printerColumns));
            }

            printerReport.push("");
        }

        if (printer.channels && !isObjectEmpty(printer.channels)) {
            printerReport.push(this.label('CHANNELS'));

            for (const [channelName, channel] of Object.entries(printer.channels)) {
                printerReport.push(this.row(`- ${channelName}`, this.currency(channel.amount || 0), printerColumns));
            }

            printerReport.push("");
        }

        const payments = Object.values(printer.payments ?? []);
        const claimeds = payments.filter(payment => !payment.unclaimed);

        if (claimeds.length) {
            printerReport.push(this.label('CLAIMED').padEnd(printerColumns, ' '));

            for (const claimed of claimeds) {
                printerReport.push(this.row(`- ${claimed.name}`, this.currency(claimed.amount || 0), printerColumns));
            }

            printerReport.push("");
        }

        const unclaimeds = payments.filter(payment => payment.unclaimed);

        if (unclaimeds.length) {
            printerReport.push(this.label('UNCLAIMED').padEnd(printerColumns, ' '));

            for (const unclaimed of unclaimeds) {
                printerReport.push(this.row(`- ${unclaimed.name}`, this.currency(unclaimed.amount || 0), printerColumns));
            }

            printerReport.push("");
        }

        if (printer.cash_movements?.sales || printer.cash_movements?.tickets) {
            printerReport.push(this.label('DRAWER').padEnd(printerColumns, ' '));

            if (printer.cash_movements?.sales) {
                printerReport.push(
                    this.row(this.label('CASH'), this.currency(printer.cash_movements.sales || 0), printerColumns),
                    ""
                );
            }

            if (printer.cash_movements?.tickets) {
                printerReport.push(this.label('TICKET').padEnd(printerColumns, ' '));

                for (const data of printer.cash_movements.tickets) {
                    const value = ((this.tilbyCurrencyPipe.transform(data.amount || 0) ?? '') + ` (${data.count})`).padEnd(10, ' ');

                    printerReport.push(this.row(`-- ${data.name}`, value, printerColumns));
                }

                printerReport.push("");
            }
        }

        if (printer.other_changes_amount) {
            printerReport.push(
                this.row(this.label('OTHER_CHANGES'), this.currency(printer.other_changes_amount), printerColumns),
                ""
            );
        }

        const extraData = [
            { name: this.label('DISCOUNTS'), amount: this.tilbyCurrencyPipe.transform(sumDiscounts(printer) ?? 0) },
            { name: this.label('SURCHARGES'), amount: this.tilbyCurrencyPipe.transform(sumSurcharges(printer) ?? 0) }
        ];

        for (const data of extraData) {
            printerReport.push(this.row(data.name, String(data.amount ?? '').padEnd(10, ' '), printerColumns));
        }

        return printerReport;
    };

}
